import ImagePlaceholderMini from '@/components/ui/icons/image-placeholder-mini'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { useGetFlags } from '@/services/flags'
import type { ParsedQuestionnaire } from '@/types/data'
import { InfoCircledIcon } from '@radix-ui/react-icons'
import { Badge as UpdatedBadgeIcon } from 'lucide-react'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Row,
  useReactTable
} from '@tanstack/react-table'
import { type Dispatch, type SetStateAction, useState } from 'react'
import ImageGalleryPreview from '@/components/common/ImageGalleryPreview'

function FlagsCell({ flags, isBatch }: { flags: Array<string>; isBatch?: boolean }) {
  const { data: flagsData } = useGetFlags()

  if (!flagsData || !flags?.length || isBatch) {
    return null
  }

  const flagTitles = flags
    .map(flag => flagsData[flag]?.title)
    .filter(Boolean)
    .join(', ')

  return (
    <p className='text-custom-neutral-800 max-w-[32ch] break-words text-sm font-normal leading-[170%]'>{flagTitles}</p>
  )
}

export const columns: ColumnDef<ParsedQuestionnaire>[] = [
  {
    accessorKey: 'title',
    header: 'Question',
    cell: ({ row }) => (
      <div>
        {row.original?.updated && (
          <UpdatedBadgeIcon
            className='h-4 w-4 mb-2 -ml-4 text-[#080F30]'
            aria-label='Answer updated'
            data-testid='updated-question-icon'
          />
        )}
        <p className='text-[#080F30] break-words max-w-[32ch] text-base font-medium leading-[170%]'>
          {row.getValue('title')}
        </p>
      </div>
    )
  },
  {
    accessorKey: 'answer',
    header: 'Answer',
    cell: ({ row }) => {
      if (row.original?.imageUploaded) {
        const imageCount = row.original?.links?.length || 1
        return (
          <div className='flex items-center gap-2'>
            <ImagePlaceholderMini />
            <span className='text-sm leading-6 font-poppins text-[#4E545E]'>
              {imageCount > 1 ? `${imageCount} Images Uploaded` : 'Image Uploaded'}
            </span>
          </div>
        )
      }

      const answer = row.getValue('answer')
      const displayAnswer = Array.isArray(answer) ? answer.join(', ') : (answer as string)
      const pulse = row.original?.type === 'shenai' && displayAnswer ? row.original.pulse : null
      const answerClass = 'text-custom-neutral-800 max-w-[32ch] break-words text-sm font-normal leading-[170%]'

      return (
        <div>
          <p className={answerClass}>{displayAnswer}</p>
          {typeof pulse === 'number' && <p className={answerClass}>Pulse: {pulse} bpm</p>}
        </div>
      )
    }
  },
  {
    accessorKey: 'flags',
    header: () => 'Flag',
    cell: ({ row }) => <FlagsCell {...row.original} />
  }
]

export function DisplayRow({
  row,
  className,
  isOpen,
  onImageClick,
  ...props
}: {
  row: Row<any>
  isOpen?: boolean
  onImageClick?: () => void
} & React.HTMLAttributes<HTMLTableRowElement>) {
  return (
    <TableRow
      className={cn(
        'even:bg-white bg-custom-gray-3 border border-transparent border-t-0 rounded-2xl last:border-r last:border-custom-neutral-200',
        !!row.original?.hasFlags && 'bg-[#FFE5E5]!',
        className
      )}
      key={row.id}
      data-state={row.getIsSelected() && 'selected'}
      data-testid={`consultation-table-row${row.original?.hasFlags ? '-with-flag' : ''}`}
      {...props}
    >
      {row.getVisibleCells().map(cell => {
        const link = row.original?.link
        const links = row.original?.links
        const hasImages = link || links

        if (hasImages) {
          // Check if it's a Zoho workdrive URL (password protected, should open in new tab)
          const isZohoUrl = link && typeof link === 'string' && link.includes('workdrive.zohoexternal.com')

          if (isZohoUrl) {
            return (
              <TableCell className='p-6' key={cell.id}>
                <a
                  href={link}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-left hover:underline cursor-pointer'
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </a>
              </TableCell>
            )
          }

          return (
            <TableCell className='p-6' key={cell.id}>
              <button type='button' onClick={onImageClick} className='text-left hover:underline cursor-pointer'>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </button>
            </TableCell>
          )
        }

        return (
          <TableCell className='p-6' key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        )
      })}
    </TableRow>
  )
}

export function DisplayRowWithDescription({
  row,
  expandedQuestion,
  setExpandedQuestion,
  onImageClick
}: {
  row: Row<any>
  expandedQuestion: string | null
  setExpandedQuestion: Dispatch<SetStateAction<string | null>>
  onImageClick?: () => void
}) {
  const isThisExpanded = expandedQuestion === row.original.title

  return (
    <>
      <DisplayRow
        row={row}
        className='cursor-pointer'
        onClick={() =>
          setExpandedQuestion(prevQuestion => (prevQuestion === row.original.title ? null : row.original.title))
        }
        onImageClick={onImageClick}
      />

      {/** nesting with tr -> td gets rid of console warnings */}
      <tr>
        <td>
          <div className='w-full h-[0px] relative'>
            <InfoCircledIcon
              className={cn(
                'h-4 w-4 shrink-0 text-muted-foreground transition-all duration-200 absolute left-2 -top-6 opacity-60',
                isThisExpanded && 'translate-y-10 text-black opacity-100'
              )}
            />
          </div>
        </td>
      </tr>

      {/** nesting with tr -> td gets rid of console warnings */}
      <tr>
        <td>
          <div
            className={cn(
              'max-w-[444px] transition-all duration-200 ease-in-out overflow-hidden max-h-[0px] opacity-0 h-[0px] border border-transparent border-t-0',
              isThisExpanded && 'max-h-full pt-6 opacity-100 h-auto px-4 pb-4'
            )}
            dangerouslySetInnerHTML={{
              __html: row.original.description
                .replace('<ul>', '<ul class="list-disc pl-4 space-y-1 text-sm text-custom-neutral-800 mt-4">')
                .replace('<p>', "<p class='mt-4'>")
            }}
          />
        </td>
      </tr>
    </>
  )
}

type Props = {
  data: ParsedQuestionnaire[]
  showOnlyUpdated?: boolean
}

export function ConsultationTable({ data, showOnlyUpdated }: Props) {
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null)
  const [galleryImages, setGalleryImages] = useState<string[] | string | null>(null)

  const tableData = showOnlyUpdated ? data.filter(question => question.updated) : data

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true
  })

  return (
    <div className='w-full'>
      <div className='rounded-md'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow className='bg-white' key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <TableHead className='p-6 font-semibold text-base leading-[170%] text-[#080F30]' key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className='border-0'>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => {
                const handleImageClick = () => {
                  const images = row.original?.links || row.original?.link
                  if (!images) return

                  // Don't open Zoho URLs in modal - they're handled by the <a> tag
                  const isZohoUrl = typeof images === 'string' && images.includes('workdrive.zohoexternal.com')
                  if (!isZohoUrl) {
                    setGalleryImages(images)
                  }
                }

                if (row.original.description) {
                  return (
                    <DisplayRowWithDescription
                      key={row.original.title}
                      row={row}
                      expandedQuestion={expandedQuestion}
                      setExpandedQuestion={setExpandedQuestion}
                      onImageClick={handleImageClick}
                    />
                  )
                }

                return <DisplayRow key={row.original.title} row={row} onImageClick={handleImageClick} />
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns?.length} className='h-24 text-center'>
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <ImageGalleryPreview images={galleryImages} onClose={() => setGalleryImages(null)} />
    </div>
  )
}
